import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { PaymentMethod, CreatePaymentMethodDto, UpdatePaymentMethodDto } from "../types/payment";
import { apiService } from "../services/paymentAPI";

interface PaymentMethodsContextType {
  paymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod | null;
  loading: boolean;
  error: string | null;
  addPaymentMethod: (paymentMethod: CreatePaymentMethodDto) => Promise<void>;
  addPayPalPaymentMethod: (paypalEmail: string) => Promise<void>;
  updatePaymentMethod: (id: string, updates: UpdatePaymentMethodDto) => Promise<void>;
  updatePayPalEmail: (id: string, newEmail: string) => Promise<void>;
  deletePaymentMethod: (id: string) => Promise<void>;
  setDefaultPaymentMethod: (id: string) => Promise<void>;
  refreshPaymentMethods: () => Promise<void>;
}

const PaymentMethodsContext = createContext<PaymentMethodsContextType | undefined>(undefined);

export const usePaymentMethods = () => {
  const context = useContext(PaymentMethodsContext);
  if (!context) {
    throw new Error("usePaymentMethods must be used within PaymentMethodsProvider");
  }
  return context;
};

export const PaymentMethodsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyPaymentMethods();

      // Handle response format
      const data = response.data || response;
      const paymentMethodsList = Array.isArray(data) ? data : [];

      setPaymentMethods(paymentMethodsList);

      // Find default payment method
      const defaultMethod = paymentMethodsList.find((method: PaymentMethod) => method.isDefault);
      setDefaultPaymentMethod(defaultMethod || null);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  }, []);

  const addPaymentMethod = async (paymentMethodData: CreatePaymentMethodDto) => {
    try {
      setLoading(true);
      setError(null);

      // Use regular payment method endpoint for all types (no Stripe validation)
      const response = await apiService.createPaymentMethod(paymentMethodData);
      
      // Refresh payment methods from server to get latest data
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to add payment method");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentMethod = async (id: string, updates: UpdatePaymentMethodDto) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.updatePaymentMethod(id, updates);
      const updatedMethod = response.data || response;

      setPaymentMethods(prev =>
        prev.map(method => method.id === id ? updatedMethod : method)
      );

      // If setting as default, update default payment method
      if (updates.isDefault) {
        setDefaultPaymentMethod(updatedMethod);
      }
      
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to update payment method");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePayPalEmail = async (id: string, newEmail: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update via API service
      const response = await apiService.updatePayPalEmail(id, newEmail);
      const updatedMethod = response.data || response;

      setPaymentMethods(prev =>
        prev.map(method => method.id === id ? updatedMethod : method)
      );
      
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to update PayPal email");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deletePaymentMethod = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.deletePaymentMethod(id);

      // Refresh payment methods from server to get latest data
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to delete payment method");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addPayPalPaymentMethod = async (paypalEmail: string) => {
    try {
      setLoading(true);
      setError(null);

      await apiService.createPayPalPaymentMethod(paypalEmail);
      
      // Refresh payment methods from server to get latest data
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to add PayPal payment method");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setDefaultPaymentMethodById = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.setDefaultPaymentMethod(id);

      // Refresh payment methods from server to get latest data
      await refreshPaymentMethods();
    } catch (err: any) {
      setError(err?.message || "Failed to set default payment method");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load payment methods on mount
  useEffect(() => {
    refreshPaymentMethods();
  }, []);

  return (
    <PaymentMethodsContext.Provider value={{
      paymentMethods,
      defaultPaymentMethod,
      loading,
      error,
      addPaymentMethod,
      addPayPalPaymentMethod,
      updatePaymentMethod,
      updatePayPalEmail,
      deletePaymentMethod,
      setDefaultPaymentMethod: setDefaultPaymentMethodById,
      refreshPaymentMethods,
    }}>
      {children}
    </PaymentMethodsContext.Provider>
  );
};
